import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Speed, Memory, Storage, NetworkCheck } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface PerformanceResult {
  endpoint: string;
  responseTime: number;
  status: 'success' | 'error';
  timestamp: Date;
  dataSize?: number;
  memoryUsage?: number;
}

interface PerformanceMetrics {
  avgResponseTime: number;
  minResponseTime: number;
  maxResponseTime: number;
  totalRequests: number;
  successRate: number;
  totalDataSize: number;
  avgMemoryUsage: number;
}

const PerformanceTest: React.FC = () => {
  const { user } = useAuth();
  const [results, setResults] = useState<PerformanceResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    avgResponseTime: 0,
    minResponseTime: 0,
    maxResponseTime: 0,
    totalRequests: 0,
    successRate: 0,
    totalDataSize: 0,
    avgMemoryUsage: 0
  });

  const testEndpoints = [
    { name: 'Health Check', url: '/health' },
    { name: 'User Profile', url: '/auth/me' },
    { name: 'Analytics Dashboards', url: '/analytics/dashboards' },
    { name: 'Marketplace Suppliers', url: '/marketplace/suppliers' },
    { name: 'Teams', url: '/teams' },
    { name: 'Referrals Stats', url: '/referrals/stats' },
    { name: 'AI Support Health', url: '/ai-support/health' },
    { name: 'Documents Contracts', url: '/documents/contracts' },
    { name: 'Finance Budget', url: '/finance/budget' }
  ];

  const measurePerformance = async (endpoint: { name: string; url: string }) => {
    const startTime = performance.now();
    const perfMem = performance as Performance & { memory?: { usedJSHeapSize?: number } };
    const startMemory = perfMem.memory?.usedJSHeapSize ?? 0;

    try {
      const response = await api.get(endpoint.url);
      const endTime = performance.now();
      const endMemory = perfMem.memory?.usedJSHeapSize ?? 0;
      
      const responseTime = endTime - startTime;
      const dataSize = JSON.stringify(response.data).length;
      const memoryUsage = endMemory - startMemory;

      const result: PerformanceResult = {
        endpoint: endpoint.name,
        responseTime,
        status: 'success',
        timestamp: new Date(),
        dataSize,
        memoryUsage
      };

      setResults(prev => [...prev, result]);
      return result;
    } catch (error) {
      const endTime = performance.now();
      const result: PerformanceResult = {
        endpoint: endpoint.name,
        responseTime: endTime - startTime,
        status: 'error',
        timestamp: new Date()
      };

      setResults(prev => [...prev, result]);
      return result;
    }
  };

  const runPerformanceTest = async (iterations: number = 5) => {
    setIsLoading(true);
    setResults([]);

    for (let i = 0; i < iterations; i++) {
      for (const endpoint of testEndpoints) {
        await measurePerformance(endpoint);
        // Небольшая задержка между запросами
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    setIsLoading(false);
    calculateMetrics();
  };

  const calculateMetrics = () => {
    const successfulResults = results.filter(r => r.status === 'success');
    const responseTimes = successfulResults.map(r => r.responseTime);
    const dataSizes = successfulResults.map(r => r.dataSize || 0);
    const memoryUsages = successfulResults.map(r => r.memoryUsage || 0);

    const avgResponseTime = responseTimes.length > 0 ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length : 0;
    const minResponseTime = responseTimes.length > 0 ? Math.min(...responseTimes) : 0;
    const maxResponseTime = responseTimes.length > 0 ? Math.max(...responseTimes) : 0;
    const totalRequests = results.length;
    const successRate = totalRequests > 0 ? (successfulResults.length / totalRequests) * 100 : 0;
    const totalDataSize = dataSizes.reduce((a, b) => a + b, 0);
    const avgMemoryUsage = memoryUsages.length > 0 ? memoryUsages.reduce((a, b) => a + b, 0) / memoryUsages.length : 0;

    setMetrics({
      avgResponseTime,
      minResponseTime,
      maxResponseTime,
      totalRequests,
      successRate,
      totalDataSize,
      avgMemoryUsage
    });
  };

  const getPerformanceGrade = (responseTime: number) => {
    if (responseTime < 100) return { grade: 'A', color: 'success' as const, label: 'Отлично' };
    if (responseTime < 300) return { grade: 'B', color: 'warning' as const, label: 'Хорошо' };
    if (responseTime < 500) return { grade: 'C', color: 'error' as const, label: 'Удовлетворительно' };
    return { grade: 'D', color: 'error' as const, label: 'Плохо' };
  };

  const getEndpointStats = (endpointName: string) => {
    const endpointResults = results.filter(r => r.endpoint === endpointName);
    if (endpointResults.length === 0) return null;

    const responseTimes = endpointResults.map(r => r.responseTime);
    const avgTime = responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
    const successCount = endpointResults.filter(r => r.status === 'success').length;
    const successRate = (successCount / endpointResults.length) * 100;

    return { avgTime, successRate, totalTests: endpointResults.length };
  };

  return (
    <Card sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
        ⚡ Тестирование производительности API
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Пользователь: {user?.email}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Backend URL: {process.env['REACT_APP_API_BASE_URL'] || 'http://localhost:3001/api'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Тестируемые endpoints: {testEndpoints.length}
        </Typography>
      </Box>

      {/* Метрики производительности */}
      {metrics.totalRequests > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            📊 Общие метрики производительности
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Speed color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="primary">
                  {metrics.avgResponseTime.toFixed(0)}ms
                </Typography>
                <Typography variant="body2">Среднее время ответа</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <NetworkCheck color="success" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="success.main">
                  {metrics.successRate.toFixed(1)}%
                </Typography>
                <Typography variant="body2">Успешность запросов</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Storage color="info" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="info.main">
                  {(metrics.totalDataSize / 1024).toFixed(1)}KB
                </Typography>
                <Typography variant="body2">Общий объем данных</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Memory color="warning" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="warning.main">
                  {(metrics.avgMemoryUsage / 1024).toFixed(1)}KB
                </Typography>
                <Typography variant="body2">Среднее использование памяти</Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Диапазон времени ответа: {metrics.minResponseTime.toFixed(0)}ms - {metrics.maxResponseTime.toFixed(0)}ms
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Всего запросов: {metrics.totalRequests}
            </Typography>
          </Box>
        </Paper>
      )}

      <Box sx={{ mb: 3 }}>
        <Button 
          variant="contained" 
          size="large"
          onClick={() => runPerformanceTest(3)}
          disabled={isLoading}
          sx={{ mr: 2 }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Тестирование...
            </>
          ) : (
            '🚀 Быстрый тест (3 итерации)'
          )}
        </Button>
        <Button 
          variant="outlined" 
          size="large"
          onClick={() => runPerformanceTest(10)}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Тестирование...
            </>
          ) : (
            '⚡ Полный тест (10 итераций)'
          )}
        </Button>
      </Box>

      {/* Результаты по endpoints */}
      {results.length > 0 && (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Endpoint</TableCell>
                <TableCell align="center">Среднее время</TableCell>
                <TableCell align="center">Успешность</TableCell>
                <TableCell align="center">Оценка</TableCell>
                <TableCell align="center">Тестов</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testEndpoints.map((endpoint) => {
                const stats = getEndpointStats(endpoint.name);
                if (!stats) return null;

                const grade = getPerformanceGrade(stats.avgTime);

                return (
                  <TableRow key={endpoint.name}>
                    <TableCell>{endpoint.name}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {stats.avgTime.toFixed(0)}ms
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={`${stats.successRate.toFixed(1)}%`}
                        color={stats.successRate > 95 ? 'success' : stats.successRate > 80 ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={`${grade.grade} (${grade.label})`}
                        color={grade.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {stats.totalTests}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Рекомендации по оптимизации */}
      {metrics.totalRequests > 0 && (
        <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            💡 Рекомендации по оптимизации
          </Typography>
          <List dense>
            {metrics.avgResponseTime > 300 && (
              <ListItem>
                <ListItemText 
                  primary="⏱️ Время ответа выше нормы"
                  secondary="Рекомендуется оптимизировать запросы к базе данных и добавить кэширование"
                />
              </ListItem>
            )}
            {metrics.successRate < 95 && (
              <ListItem>
                <ListItemText 
                  primary="❌ Низкая успешность запросов"
                  secondary="Проверьте стабильность backend и обработку ошибок"
                />
              </ListItem>
            )}
            {metrics.avgMemoryUsage > 1024 * 1024 && (
              <ListItem>
                <ListItemText 
                  primary="💾 Высокое потребление памяти"
                  secondary="Оптимизируйте размер передаваемых данных и добавьте пагинацию"
                />
              </ListItem>
            )}
            {metrics.avgResponseTime < 100 && metrics.successRate > 95 && (
              <ListItem>
                <ListItemText 
                  primary="✅ Отличная производительность"
                  secondary="Система работает оптимально, продолжайте мониторинг"
                />
              </ListItem>
            )}
          </List>
        </Paper>
      )}

      {results.length === 0 && !isLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Нажмите кнопку для начала тестирования производительности API
        </Typography>
      )}
    </Card>
  );
};

export default PerformanceTest;
